import { useEffect, useMemo, useState } from "react";
import { SKINS, getSkin, type Skin } from "@/lib/skins";
import { applySkin } from "@/lib/applySkin";
import { useBranch } from "@/store/branch";

// Demo slug that unlocks the in-app skin chooser. Mirrors the v1
// behavior on `menu.tappya.com/208`, which showed a floating palette.
// Visitors of any other branch never see this UI.
const DEMO_SLUGS = new Set(["208"]);

export function FloatingSkinSelector() {
    const { branchSlug, branchConfig } = useBranch();
    const [open, setOpen] = useState(false);
    const [activeId, setActiveId] = useState<string>(() => {
        const initial = (branchConfig as { theme?: string | null } | null)?.theme ?? null;
        return getSkin(initial)?.id ?? SKINS[0].id;
    });

    // When the branchConfig finishes loading and brings a saved skin,
    // adopt it as the initial highlight (without re-applying — the
    // BranchProvider already did that).
    useEffect(() => {
        const saved = (branchConfig as { theme?: string | null } | null)?.theme ?? null;
        const next = getSkin(saved)?.id;
        if (next) setActiveId(next);
    }, [branchConfig]);

    const visible = !!branchSlug && DEMO_SLUGS.has(branchSlug);
    const grouped = useMemo(() => {
        return {
            light: SKINS.filter((s) => s.mode === "light"),
            dark: SKINS.filter((s) => s.mode === "dark"),
        };
    }, []);

    if (!visible) return null;

    const onPick = (skin: Skin) => {
        setActiveId(skin.id);
        applySkin(skin);
        setOpen(false);
    };

    return (
        <div
            style={{
                position: "fixed",
                right: 16,
                bottom: "calc(env(safe-area-inset-bottom, 0px) + 88px)",
                zIndex: 9000,
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                gap: 10,
                pointerEvents: "none",
            }}
        >
            {open && (
                <div
                    style={{
                        pointerEvents: "auto",
                        background: "var(--surface-1)",
                        color: "var(--ink-1)",
                        border: "1px solid var(--border)",
                        borderRadius: 16,
                        boxShadow: "0 16px 36px -12px rgba(0,0,0,0.30)",
                        padding: 12,
                        width: 260,
                        maxHeight: "60vh",
                        overflowY: "auto",
                    }}
                >
                    <div
                        style={{
                            fontSize: 12,
                            fontWeight: 700,
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                            color: "var(--ink-3)",
                            margin: "2px 4px 8px",
                        }}
                    >
                        Try a skin
                    </div>

                    {(["light", "dark"] as const).map((mode) => (
                        <div key={mode} style={{ marginBottom: 8 }}>
                            <div
                                style={{
                                    fontSize: 10.5,
                                    fontWeight: 700,
                                    color: "var(--ink-3)",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.06em",
                                    margin: "4px 4px 6px",
                                }}
                            >
                                {mode === "light" ? "Light" : "Dark"}
                            </div>
                            {grouped[mode].map((skin) => {
                                const selected = activeId === skin.id;
                                return (
                                    <button
                                        key={skin.id}
                                        type="button"
                                        onClick={() => onPick(skin)}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 10,
                                            width: "100%",
                                            padding: "8px 10px",
                                            background: selected ? "var(--surface-2)" : "transparent",
                                            border: 0,
                                            borderRadius: 10,
                                            cursor: "pointer",
                                            color: "var(--ink-1)",
                                            fontFamily: "inherit",
                                            textAlign: "left",
                                        }}
                                    >
                                        <span
                                            aria-hidden
                                            style={{
                                                width: 20,
                                                height: 20,
                                                borderRadius: 999,
                                                background: skin.tokens["--primary-600"],
                                                border: "2px solid var(--surface-1)",
                                                boxShadow: "0 0 0 1px var(--border)",
                                                flexShrink: 0,
                                            }}
                                        />
                                        <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>
                                            {skin.name}
                                        </span>
                                        {selected && (
                                            <span
                                                aria-hidden
                                                style={{ color: "var(--primary-600)", fontWeight: 800 }}
                                            >
                                                ✓
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    ))}
                </div>
            )}

            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-label="Choose a skin"
                style={{
                    pointerEvents: "auto",
                    width: 48,
                    height: 48,
                    borderRadius: 999,
                    border: 0,
                    background: "var(--primary-600)",
                    color: "#fff",
                    boxShadow: "0 8px 20px -6px rgba(0,0,0,0.35)",
                    cursor: "pointer",
                    fontSize: 22,
                    display: "grid",
                    placeItems: "center",
                }}
            >
                {/* Palette glyph */}
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <circle cx="13.5" cy="6.5" r="0.5" />
                    <circle cx="17.5" cy="10.5" r="0.5" />
                    <circle cx="8.5" cy="7.5" r="0.5" />
                    <circle cx="6.5" cy="12.5" r="0.5" />
                    <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.83 0 1.5-.67 1.5-1.5 0-.39-.15-.74-.39-1.01-.23-.26-.36-.62-.36-.99 0-.83.67-1.5 1.5-1.5H16c3.31 0 6-2.69 6-6 0-5.5-4.5-10-10-10z" />
                </svg>
            </button>
        </div>
    );
}
